import { FC, useState } from 'react';
import { useRouter } from 'next/router';
import styled from 'styled-components';

import { useNotices } from '@/features/notices/useNotices';
import { useCases } from '@/features/vfo/hooks/useCases';
import { useDocuments } from '@/features/vfo/hooks/useDocuments';
import { useTasks } from '@/features/vfo/hooks/useTasks';
import { breakpoints, media } from '@/shared/components';
import { COLORS } from '@/shared/constants/colors';
import { Button } from '@/shared/uikitV2/Button';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';
import { useModalsStore } from '@/widgets/modals/modals.store';
import { DeleteCaseModalProps } from '@/widgets/modals/types';

import { ModalContainerV2 } from '../../interwill/containers/ModalContainerV2';

const INFO_CARDS_COUNT = 2;

export const DeleteCaseModal: FC<DeleteCaseModalProps> = ({ caseId }) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'deleteCase' });

  const [isLoading, setIsLoading] = useState(false);
  const notices = useNotices();
  const { closeModal } = useModalsStore();
  const { deleteCase } = useCases();
  const { fetchDocuments } = useDocuments();
  const { fetchTasks } = useTasks();
  const router = useRouter();

  const handleDelete = async () => {
    setIsLoading(true);
    const success = await deleteCase(caseId);
    fetchDocuments();
    fetchTasks();
    setIsLoading(false);

    if (success) {
      router.push('/vfo/cases');
      notices.success(t('successNotice'));
      closeModal();
    }
  };

  return (
    <ModalContainerV2 pinBottom title={t('title')}>
      <ModalBody>
        <span>{t('text')}</span>
        {new Array(INFO_CARDS_COUNT).fill(null).map((_, index) => (
          <InfoCard key={index}>
            <InfoCardTitle>{t(`infoCard${index + 1}.title`)}</InfoCardTitle>
            <p>{t.rich(`infoCard${index + 1}.text`)}</p>
          </InfoCard>
        ))}
      </ModalBody>
      <Buttons>
        <Button type="default" size="middle" onClick={closeModal}>
          {t('cancel')}
        </Button>
        <ButtonDestructive
          size="middle"
          onClick={handleDelete}
          loading={isLoading}
          disabled={isLoading}
        >
          {t('delete')}
        </ButtonDestructive>
      </Buttons>
    </ModalContainerV2>
  );
};

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow-y: auto;
  padding: 24px 0;
  font-size: 14px;
`;

const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: 16px;
  gap: 16px;

  ${media.md}, ${breakpoints.sm} {
    flex-direction: column-reverse;
  }
`;

const ButtonDestructive = styled(Button)`
  &&& {
    background: ${COLORS.systemFlame200};
    color: ${COLORS.white};

    &:hover {
      background: ${COLORS.systemFlame300} !important;
    }
  }
`;

const InfoCard = styled.div`
  padding: 16px;
  background: ${COLORS.systemLight200};
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const InfoCardTitle = styled.p`
  font-weight: 700;
`;
